import React, { useState, useEffect } from 'react';
import Input from '../../ui/input';
import Textarea from '../../ui/textarea';
import { validateSite } from '../../../model/SiteModel';
import { getSites, siteAdd, siteDrawingUpload } from '../../../redux/api/siteAPI';
import Button from '../../ui/button';
import MapboxMap from '../../map/MapboxMap';
import { getCoordinatesFromAddress } from '../../../utils/geocoding';
import { set } from 'lodash';

import { useTranslation } from 'react-i18next';

const AddSiteModal = ({ isOpen, onClose }) => {
    const {t} = useTranslation('Sites');
    const [formData, setFormData] = useState({
        Name: '',
        Description: '',
        location: '',
        Work_hours:null,
        nbr_workers:null,
        lat: null,
        lng: null
    });
    const [error, setError] = useState();
    const [formErrors, setFormErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [showMap, setShowMap] = useState(false); // State to manage showing/hiding map
    const [sitesList, setSitesList] = useState([]);
    const [drawingFile, setDrawingFile] = useState(null);
    const [drawingPreview, setDrawingPreview] = useState(null);

    useEffect(() => {
        if (isOpen && showMap && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                setFormData(prevState => ({
                    ...prevState,
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }));
            });
        }
        else if (!showMap)
            setFormData(prevState => ({
                ...prevState,
                lat: null,
                lng: null
            }));
    }, [isOpen, showMap]);

    const closeModal = (refresh, options) => {
        setFormData({
            Name: '',
            Description: '',
            location: '',
            Work_hours:null,
            nbr_workers:null,
            lat: null,
            lng: null
        });
        setShowMap(false); // Reset showMap state when closing modal
        setDrawingFile(null);
        setDrawingPreview(null);
        onClose(refresh, options);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (formErrors[name]) {
            setFormErrors(prevState => ({
                ...prevState,
                [name]: ''
            }));
        }
    };

    const handleLocationSelect = (location) => {
        setFormData(prevState => ({
            ...prevState,
            lat: location.lat,
            lng: location.lng
        }));
    };

    const handleDrawingChange = (e) => {
        const file = e.target.files?.[0] || null;
        setDrawingFile(file);
        if (drawingPreview) URL.revokeObjectURL(drawingPreview);
        if (file && file.type.startsWith("image/")) {
            setDrawingPreview(URL.createObjectURL(file));
        } else {
            setDrawingPreview(null);
        }
    };

    const handleGeocodeAddress = async () => {
        try {
            const { lat, lng } = await getCoordinatesFromAddress(formData.location);
            setFormData(prevState => ({
                ...prevState,
                lat: lat,
                lng: lng
            }));
            setShowMap(true); // Show map after successful geocoding
        } catch (error) {
            console.error('Error geocoding address:', error);
            setFormErrors(prevState => ({
                ...prevState,
                location: 'Unable to determine coordinates from the address'
            }));
        }
    };

    const validateForm = async () => {
        try {
            await validateSite(formData);
            return true;
        } catch (error) {
            const validationErrors = {};
            error.inner.forEach(err => {
                validationErrors[err.path] = err.message;
            });
            setFormErrors(validationErrors);
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = await validateForm();
        if (isValid) {
            let timeout;
            try {
                setLoading(true);
                setLoadingText("Adding Site...");
                timeout = setTimeout(() => {
                    setLoadingText("This is taking longer than usual. Please wait.");
                }, 5000);
                const result = await siteAdd(formData);
                if (result.data) {
                    const siteId = result.data.site_id || result.data.site?.Site_ID;
                    if (siteId && drawingFile) {
                        const uploadResult = await siteDrawingUpload(siteId, drawingFile);
                        if (uploadResult.error) {
                            setError(uploadResult.error);
                            setLoading(false);
                            clearTimeout(timeout);
                            return;
                        }
                    }
                    closeModal(true, { resetPage: true });
                } else {
                    setError(result.error);
                }
                setLoading(false);
                clearTimeout(timeout);
            } catch (error) {
                console.error("Error adding site:", error);
                setError("An unexpected error occurred while adding the site.");
                setLoading(false);
                clearTimeout(timeout);
            }
        }
    };
    useEffect(() => {
        const fetchSites = async () => {
            try {
                const { error, data } = await getSites();
                if (error) {
                    throw new Error(error);
                }
                setSitesList(data.sites);
            } catch (err) {
                setError(err);
            }
        };
        fetchSites();
        return (() => {
            setFormErrors({});
            setSitesList([])
            setError(null)
            setFormData(null)
            setLoading(false)
            setShowMap(false)
            setLoadingText(false)
        })
    }, []);
    const toggleMap = () => {
        setShowMap(!showMap); // Toggle showMap state
    };
    if (sitesList)

        return (
            <form data-testid="site-add-form" onSubmit={handleSubmit}>
                <div className="grid gap-7 max-lg:grid-cols-1 grid-cols-2 w-auto m-2 bg-white max-md:p-3 p-8 rounded-3xl shadow-lg">
                    <p className="flex justify-center col-span-full text-xl text-[#4C4C66] font-bold mb-4">{t('add_site_details')}</p>
                    
                    {/* Left Column */}
                    <div className="flex flex-col gap-4">
                        <Input
                            required
                            label={t('name')}
                            name="Name"
                            value={formData.Name}
                            onChange={handleChange}
                            placeholder={t('enter_site_name')}
                            error={formErrors.Name}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                required
                                label={t('work_hours')}
                                name="Work_hours"
                                value={formData.Work_hours}
                                onChange={handleChange}
                                placeholder={t('enter_work_hours_in_a_day_per_worker')}
                                error={formErrors.Work_hours}
                            />
                            <Input
                                required
                                label={t('number_of_workers')}
                                name="nbr_workers"
                                value={formData.nbr_workers}
                                onChange={handleChange}
                                placeholder={t('enter_number_of_workers')}
                                error={formErrors.nbr_workers}
                            />
                        </div>
                        <Textarea
                            required
                            label={t('address')}
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder={t('enter_site_address')}
                            error={formErrors.location}
                            maxLength={100}
                            rows={2}
                        />
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-end'>
                            <Button
                                className={'my-1'}
                                disabled={!formData.location}
                                type="button"
                                onClick={handleGeocodeAddress}
                                color={"secondary"}
                            >{t('drop_a_pin')}</Button>
                            <Input
                                label={t('latitude')}
                                name="lat"
                                type="number"
                                value={formData.lat || ''}
                                onChange={handleChange}
                                placeholder={t('enter_latitude')}
                            />
                            <Input
                                label={t('longitude')}
                                name="lng"
                                type="number"
                                value={formData.lng || ''}
                                onChange={handleChange}
                                placeholder={t('enter_longitude')}
                            />
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="flex flex-col gap-4">
                        <Textarea
                            label={t('description')}
                            name="Description"
                            value={formData.Description}
                            onChange={handleChange}
                            placeholder={t('enter_site_description')}
                            error={formErrors.Description}
                            maxLength={200}
                        />
                        <div className="flex flex-col gap-2">
                            <label className="text-sm text-[#4C4C66] pb-1 pl-2">Site drawing</label>
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,application/pdf"
                                onChange={handleDrawingChange}
                                className="rounded-xl bg-[#F4F6FA] p-3 text-sm text-[#4C4C66]"
                            />
                            {drawingPreview && (
                                <img
                                    src={drawingPreview}
                                    alt="Site drawing preview"
                                    className="max-h-40 rounded-lg border object-contain"
                                />
                            )}
                            {drawingFile && !drawingPreview && (
                                <p className="text-xs text-gray-500">{drawingFile.name}</p>
                            )}
                        </div>
                        <div className='pb-5'>
                            <p className='text-sm text-[#4C4C66] pb-1 pl-2'>{t('map')}</p>
                            <MapboxMap
                                onLocationSelect={handleLocationSelect}
                                initialLat={formData.lat}
                                initialLng={formData.lng}
                                initialZoom={14}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="col-span-full flex justify-center space-x-5">
                            <span className="my-4 ml-2 text-xs text-[#FF0000]">{t('error_creating_site')}</span>
                        </div>
                    )}
                    <div className='col-span-full flex justify-center mt-6 space-x-5'>

                        <Button
                            disabled={sitesList.length < 1}
                            type="button" // Changed to type="button"
                            onClick={()=>closeModal(false)} // Close modal on button click
                            color={"cancel"}
                        >{t('cancel')}</Button>
                        <Button
                            data-testid="site-add-submit"
                            disabled={loading}
                            type="submit"
                        >
                            {loading ? loadingText : 'Add Site'}
                        </Button>
                    </div>
                </div>
            </form>
        );
};

export default AddSiteModal;
